<template>
    <div class="vehicle-map">
        <div v-if="!isEnabled" class="vehicle-map__show-on-click" @click="enable">
            <button class="button button--primary">{{ $t('vehicle_map.show_map') }}</button>
        </div>
        <GmapMap
            v-if="isEnabled"
            ref="map"
            :center="initCenter"
            :zoom="DEFAULT_ZOOM_LEVEL"
            :options="mapOptions"
            class="vehicle-map__map"
        >
            <GmapInfoWindow
                v-if="selectedVehicle !== null"
                :position="infoWindowPosition"
                @closeclick="selectedVehicle = null"
            >
                <VehicleCard
                    :show-matched-dates="true"
                    :vehicle="selectedVehicle"
                    :index-in-list="-1"
                    origin="map"
                    size="tiny"
                    class="vehicle-map__popup-card"
                />
            </GmapInfoWindow>
            <GmapCluster :options="clusterOptions">
                <GmapMarker
                    v-for="(marker, index) in vehicleCardMarkers"
                    :key="index"
                    :position="marker.position"
                    :label="marker.label"
                    :icon="marker.icon"
                    :visible="!selectedVehicle || marker.vehicle.id !== selectedVehicle.id"
                    @click="displayVehicleCard(marker)"
                />
            </GmapCluster>
            <GmapMarker v-for="(marker, index) in simpleMarkers" :key="index" :position="marker.position" />
        </GmapMap>
    </div>
</template>

<script>
import { components } from 'gmap-vue';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { getFilterValue } from '~/helpers/queryParser';
import { AVAILABLE_BETWEEN, LOCATION } from '~/config/filterKeys';

import allowedCountriesDetails from '~/config/allowedCountriesDetails';
import namedColors from '~/config/namedColors';

import VehicleCard from '~/components/vehicles/VehicleCard.vue';

/**
 * Default zoom level
 * @type integer
 */
export const DEFAULT_ZOOM_LEVEL = 4;

/**
 * Zoom level to change to when the center property is changed
 * @type integer
 */
const CENTER_UPDATE_ZOOM_LEVEL = 8;

export default {
    components: {
        VehicleCard,
        GmapCluster: components.Cluster,
    },
    props: {
        center: {
            type: Object,
            default: null,
        },
        vehicles: {
            type: Array,
            default: () => [],
        },
        markerType: {
            type: String,
            default: 'vehicle-card', // 'vehicle-card' or 'simple'
        },
        showOnClick: {
            type: Boolean,
            default: false,
        },
        zoomLevel: {
            type: Number,
            default: null,
        },
        country: {
            type: String,
            default: null,
        },
    },
    data() {
        const country = this.country || this.$store.getters.siteCountryCode;
        const countryDetails = allowedCountriesDetails.find(({ code }) => code === country) || {};

        // Get map data from allowedCountryDetails or fallback to default values
        const { mapCenter = { lat: 60.22, lng: 16.32 }, mapZoom = 4 } = countryDetails;

        return {
            initCenter: this.center || mapCenter,
            defaultCenter: mapCenter,
            clusterOptions: {
                renderer: {
                    render({ count, position }) {
                        let color;

                        if (count > 300) {
                            color = '#AD4A49';
                        } else if (count > 100) {
                            color = '#C72B1E';
                        } else if (count > 10) {
                            color = '#EC6564';
                        } else {
                            color = '#EA9394';
                        }

                        // create svg url with fill color
                        const svg = window.btoa(`
  <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
    <circle cx="120" cy="120" opacity="1" r="70" />
    <circle cx="120" cy="120" opacity=".5" r="90" />
    <circle cx="120" cy="120" opacity=".2" r="110" />
  </svg>`);

                        // create marker using svg icon
                        /* global google */
                        return new google.maps.Marker({
                            position,
                            icon: {
                                url: `data:image/svg+xml;base64,${svg}`,
                                scaledSize: new google.maps.Size(45, 45),
                            },
                            label: {
                                text: String(count),
                                color: 'rgba(0, 0, 0, 0.8)',
                                fontSize: '12px',
                            },
                            title: `Cluster of ${count} markers`,
                            // adjust zIndex to be above other markers
                            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                        });
                    },
                },
            },
            isEnabled: !this.showOnClick,
            mapOptions: {
                mapTypeControl: false,
                fullscreenControl: false,
                streetViewControl: false,
                zoomControlOptions: {
                    position: 1, // google.maps.ControlPosition.TOP_LEFT
                },
            },
            infoWindowPosition: null,
            selectedVehicle: null,
            markers: [],
            DEFAULT_ZOOM_LEVEL: this.zoomLevel || mapZoom,
        };
    },
    computed: {
        ...mapGetters(['cmsPage']),
        selectedDates() {
            return getFilterValue(this.$route.query, AVAILABLE_BETWEEN);
        },
        searchLocationCoordinates() {
            const location = getFilterValue(this.$route.query, LOCATION) || this.cmsPage?.searchSettings?.location;

            if (location) {
                return {
                    lat: location.lat,
                    lng: location.lng,
                };
            }

            return location;
        },
        locationCoordinates() {
            if (!this.searchLocationCoordinates) {
                return this.defaultCenter;
            }

            return this.searchLocationCoordinates;
        },
        vehicleCardMarkers() {
            if (this.markerType !== 'vehicle-card') {
                return [];
            }

            return this.markers;
        },
        simpleMarkers() {
            if (this.markerType !== 'simple') {
                return [];
            }

            return this.markers;
        },
    },
    watch: {
        searchLocationCoordinates: {
            handler: 'updateCenter',
        },
        vehicles: {
            handler: 'updateMarkers',
        },
    },
    mounted() {
        if (!this.showOnClick) {
            this.enable();
        }
    },
    methods: {
        enable() {
            this.isEnabled = true;

            Vue.nextTick(() => {
                this.updateMarkers();

                if (!this.center && this.searchLocationCoordinates) {
                    this.updateCenter(this.searchLocationCoordinates);
                }
            });
        },
        updateMarkers() {
            if (!this.isEnabled) {
                return;
            }

            const markerSymbol = {
                path: 'M64 25.6l-7.4 5.7-7.4-5.7H19.4c-2.3 0-4.1-1.4-4.1-3.2v-18c0-1.8 1.8-3.2 4.1-3.2h74.3c2.3 0 4.1 1.4 4.1 3.2v18c0 1.8-1.8 3.2-4.1 3.2H64z',
                fillColor: 'white',
                fillOpacity: 1,
                labelOrigin: { x: 55, y: 13 },
                anchor: { x: 35, y: 30 },
                strokeColor: namedColors.primary,
                strokeOpacity: 0.5,
                strokeWeight: 0.3,
            };

            const vehicleToMarker = vehicle => {
                if (this.markerType === 'simple') {
                    return {
                        position: {
                            lat: vehicle.latitude,
                            lng: vehicle.longitude,
                        },
                    };
                }

                const priceLabel = this.$root.$options.filters.formatAsPrice(
                    this.$utils.vehicleCardPrice(vehicle, this.selectedDates),
                    vehicle.currency,
                    0
                );

                return {
                    vehicle,
                    position: {
                        lat: vehicle.latitude,
                        lng: vehicle.longitude,
                    },
                    icon: markerSymbol,
                    label: {
                        color: namedColors.primary,
                        fontWeight: 'bold',
                        fontFamily: 'Poppins',
                        fontSize: '13px',
                        text: priceLabel,
                    },
                };
            };

            this.$refs.map.$mapPromise
                .then(() => {
                    this.markers = this.vehicles.map(vehicleToMarker);
                })
                .catch(err => {
                    this.$sentry.captureException(err);
                });
        },
        updateCenter(newCoordinates) {
            if (!this.isEnabled) {
                return;
            }

            this.$refs.map.$mapPromise
                .then(map => {
                    const coords = newCoordinates || this.defaultCenter;

                    if (coords.lat && coords.lng && coords !== this.defaultCenter) {
                        // Zoom in on the new center position
                        map.setZoom(CENTER_UPDATE_ZOOM_LEVEL);
                        map.setCenter(coords);
                    } else {
                        map.setZoom(DEFAULT_ZOOM_LEVEL);
                        map.setCenter(this.defaultCenter);
                    }
                })
                .catch(err => {
                    this.$sentry.captureException(err);
                });
        },
        async displayVehicleCard(marker) {
            try {
                const data = await this.$vehicleService.getVehicles({ id: `${marker.vehicle.id}` });

                this.infoWindowPosition = marker.position;
                this.selectedVehicle = data.vehicles && data.vehicles[0] ? data.vehicles[0] : null;
            } catch (error) {
                this.$sentry.captureException(error);
            }
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.vehicle-map {
    &__show-on-click {
        width: 100%;
        height: 100%;
        background: $gray-dark;
        color: $white;
        text-align: center;
        box-sizing: border-box;
        padding-top: 175px; // To v-align the button
        background: url('~/assets/images/map-placeholder.jpg');
        background-size: cover;
        background-position: left top;
        cursor: pointer;
        border-radius: $default-border-radius;
    }

    .gm-style-iw-d {
        overflow: hidden !important;
    }

    .gm-style-iw-c {
        padding: 0 !important;
        overflow: visible !important;

        // The cross
        > button {
            z-index: 10000;
            background: white !important;
            right: -15px !important;
            top: -15px !important;
            padding: 0 !important;
            border: 1px solid $gray !important;
            border-radius: 50% !important;

            img {
                width: 16px !important;
                height: 16px !important;
                margin: 6px !important;
            }

            &.gm-ui-hover-effect {
                opacity: 1 !important;
            }
        }
    }

    &__map {
        width: 100%;
        height: 100%;
    }

    &__popup-card.vehicle-card {
        background: none;
        width: 260px;

        .vehicle-card__dates {
            top: -1px;
            left: -1px;
        }
    }

    .vue-map {
        ::v-deep .gmnoprint {
            div:first-child {
                border-radius: $default-border-radius !important;
            }
        }
    }
}
</style>
